import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  logManualQuote,
  type ConversationDetail,
  type LogQuoteRequest,
  type LogQuoteResult,
} from '@/lib/api';
import { conversationQueryKey } from './useConversation';

/**
 * useLogQuote — owner-side manual quote logging. Used when the owner picked
 * up the call themselves (forward dipped through Shelby, or the customer
 * called the personal cell, or any other off-Shelby path) and wants to
 * record the quote so the contact card and reports reflect it.
 *
 * When send_recap=true (default in the modal) the Worker also fires the
 * v2 recap workflow + drip enrollment, exactly mirroring Shelby's analyzer
 * path — so a quote logged here gets the same follow-up cadence as a
 * Shelby-handled quote.
 *
 * Optimistically flips the conversation status to `quoted` on success
 * so the UI updates without waiting for the GHL refetch.
 */
export function useLogQuote(slug: string, conversationId: string) {
  const qc = useQueryClient();
  const key = conversationQueryKey(slug, conversationId);

  return useMutation<
    LogQuoteResult,
    Error,
    Omit<LogQuoteRequest, 'conversationId' | 'slug'>
  >({
    mutationFn: (args) =>
      logManualQuote({
        ...args,
        conversationId,
        slug,
      }),
    onSuccess: () => {
      qc.setQueryData<ConversationDetail>(key, (curr) => {
        if (!curr) return curr;
        // Don't downgrade a booked thread — owner may be logging a quote
        // amount on an already-booked job for reporting purposes.
        if (curr.status === 'booked') return curr;
        return { ...curr, status: 'quoted' };
      });
      void qc.invalidateQueries({ queryKey: ['conversations', slug] });
      void qc.invalidateQueries({ queryKey: key });
      // 2026-06-02 (cowork cache-coherence patch) — also bump Today's
      // "quotes_sent" + Needs You attention count. handlePwaLogQuote
      // writes the quote dollar amount and flips contact state, both of
      // which surface in /pwa/today + /pwa/attention-count. Mirrors
      // useCreateQuote's invalidation set so the in-thread log path and
      // the cold-call FAB path keep the dashboard in sync identically.
      void qc.invalidateQueries({ queryKey: ['today', slug] });
      void qc.invalidateQueries({ queryKey: ['attention-count', slug] });
    },
  });
}
